import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend } from "recharts";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import { tokens } from "../../theme";
import { ngrWaterfall, ngrByChannel } from "../../data/mockData";

const mono = '"IBM Plex Mono", monospace';

export default function Revenue() {
  return (
    <Box>
      <PageHeader title="Revenue & Profitability" crumb="Monitor / Revenue" subtitle="Dual-currency — cash-in is receipts, not revenue" />

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid item xs={6} md={2.4}><MiniStat label="Cash In" value="$42.1k" note="receipts" /></Grid>
        <Grid item xs={6} md={2.4}><MiniStat label="Prize redemptions" value="$11.2k" note="payout" /></Grid>
        <Grid item xs={6} md={2.4}><MiniStat label="GGR (mgmt)" value="$30.9k" delta="+14%" up color={tokens.primary} /></Grid>
        <Grid item xs={6} md={2.4}><MiniStat label="Bonus cost (true)" value="$3.9k" note="exp. redemption" /></Grid>
        <Grid item xs={12} md={2.4}><MiniStat label="NGR" value="$14.6k" delta="+12%" up color={tokens.secondary} /></Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1.5, mb: 1.75, borderRadius: "10px", border: `1px solid rgba(245,166,35,0.3)`, background: "rgba(245,166,35,0.08)" }}>
        <WarningAmberRoundedIcon sx={{ color: tokens.amber, fontSize: 18 }} />
        <Typography variant="caption" color="text.secondary">
          Bonus cost = <b>expected redemption value</b> (promo SC × historical redemption rate), not face value. GGR/NGR are management definitions — reconcile with finance.
        </Typography>
      </Box>

      <Grid container spacing={1.75}>
        <Grid item xs={12} lg={7}>
          <SectionCard title="NGR waterfall" subtitle="Today">
            {ngrWaterfall.map((r) => (
              <Box key={r.label} sx={{ mb: 1.4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">{r.label}</Typography>
                  <Typography sx={{ fontFamily: mono, fontWeight: 600, fontSize: 12, color: r.value < 0 ? tokens.danger : tokens.text }}>
                    {r.value < 0 ? "-" : ""}${Math.abs(r.value).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ height: 7, borderRadius: 4, background: tokens.bg, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${r.pct}%`, background: r.color, borderRadius: 4 }} />
                </Box>
              </Box>
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SectionCard title="NGR by channel" subtitle="Share today">
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ngrByChannel} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={3} stroke="none">
                    {ngrByChannel.map((c) => <Cell key={c.name} fill={c.color} />)}
                  </Pie>
                  <RTooltip contentStyle={{ background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 8 }} />
                  <Legend verticalAlign="bottom" iconType="circle" formatter={(v) => <span style={{ color: tokens.textDim, fontSize: 12 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
